import { aplicarTema, temaEscuroAtivo } from "../../../utils/tema";
import React from "react";
import {
  MdAutoAwesome,
  MdArrowForward,
  MdDarkMode,
  MdLightMode,
  MdCheckCircle,
  MdWarningAmber,
  MdInfoOutline,
  MdErrorOutline,
  MdSearch,
  MdTrendingUp,
  MdReceiptLong,
  MdInsights,
  MdBolt,
  MdAccessTime,
} from "react-icons/md";

/* ════════════════════════════════════════════════════════════════════════════
 * Portal DataValue · Design System (showcase ao vivo)
 * Demonstra tokens e componentes da marca em light e dark. Espelha
 * BRANDING-BOOK.md + DESIGN-SYSTEM.md. Página de referência, sem efeitos colaterais.
 * ════════════════════════════════════════════════════════════════════════════ */

// ── Variáveis de tema para os frames de comparação light × dark ───────────────
const LIGHT_VARS = {
  "--dv-canvas": "#F4F7FE",
  "--dv-surface": "#FFFFFF",
  "--dv-surface-muted": "#F8F9FC",
  "--dv-border": "rgba(17,28,68,0.10)",
  "--dv-text": "#0B1437",
  "--dv-text-muted": "#4A5578",
  "--dv-text-subtle": "#707EAE",
  "--dv-focus": "#1466E3",
} as React.CSSProperties;

const DARK_VARS = {
  "--dv-canvas": "#070710",
  "--dv-surface": "#0D0E1C",
  "--dv-surface-muted": "#15172A",
  "--dv-border": "rgba(255,255,255,0.10)",
  "--dv-text": "#FFFFFF",
  "--dv-text-muted": "#A9B1D1",
  "--dv-text-subtle": "#707EAE",
  "--dv-focus": "#1ED4F9",
} as React.CSSProperties;

const DesignSystemPage: React.FC = () => {
  const [dark, setDark] = React.useState(temaEscuroAtivo);

  const toggleTheme = () => {
    const escuro = !temaEscuroAtivo();
    aplicarTema(escuro);
    setDark(escuro);
  };

  return (
    <section className="animate-fade-up mx-auto w-full max-w-[1200px] pb-10 pt-3">
      {/* ── Hero ──────────────────────────────────────────────────────────── */}
      <header className="dv-aura relative overflow-hidden rounded-4xl bg-ink-900 p-8 text-white shadow-dv-lg sm:p-10">
        <div className="nina-hero-dome pointer-events-none absolute inset-x-0 -bottom-[20%] h-[80%]" />
        <div className="pointer-events-none absolute -right-16 -top-24 h-72 w-72 rounded-full bg-dv-magenta-500/20 blur-3xl" />
        <div className="pointer-events-none absolute -left-10 bottom-0 h-56 w-56 rounded-full bg-dv-cyan-500/20 blur-3xl" />

        <div className="relative z-10 flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
          <div className="max-w-2xl">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1 text-xs font-bold uppercase tracking-wider text-white ring-1 ring-white/15 backdrop-blur">
              <MdAutoAwesome className="h-3.5 w-3.5" aria-hidden="true" /> Design System
            </span>
            <h1 className="mt-4 text-3xl font-extrabold leading-tight tracking-tight sm:text-4xl">
              Portal <span className="dv-gradient-text dv-animated-gradient bg-[length:200%_200%]">DataValue</span>
            </h1>
            <p className="mt-3 max-w-xl text-sm leading-relaxed text-white/70">
              A linguagem visual da plataforma de inteligência de dados — base neutra,
              gradiente-assinatura magenta&nbsp;→&nbsp;azul&nbsp;→&nbsp;ciano e a{" "}
              <strong className="font-semibold text-white">NINA</strong>, o copiloto de IA.
              Tudo em white e dark mode.
            </p>
            <div className="mt-5 flex flex-wrap items-center gap-2.5">
              <span className="rounded-lg bg-white/10 px-2.5 py-1 font-mono text-[11px] text-white/80 ring-1 ring-white/10">
                BRANDING-BOOK.md
              </span>
              <span className="rounded-lg bg-white/10 px-2.5 py-1 font-mono text-[11px] text-white/80 ring-1 ring-white/10">
                DESIGN-SYSTEM.md
              </span>
              <span className="rounded-lg bg-white/10 px-2.5 py-1 font-mono text-[11px] text-white/80 ring-1 ring-white/10">
                tailwind.config.js · index.css
              </span>
            </div>
          </div>

          <button
            type="button"
            onClick={toggleTheme}
            className="dv-sheen group flex shrink-0 items-center gap-2 self-start rounded-xl bg-white/10 px-4 py-2.5 text-sm font-semibold text-white ring-1 ring-white/15 backdrop-blur transition-all hover:bg-white/15 active:scale-[.98]"
          >
            {dark ? <MdLightMode className="h-4 w-4" /> : <MdDarkMode className="h-4 w-4" />}
            {dark ? "Ver em light" : "Ver em dark"}
          </button>
        </div>
      </header>

      {/* ── 1. Light × Dark lado a lado ───────────────────────────────────── */}
      <Section
        index="01"
        title="White & Dark mode"
        desc="A mesma identidade, duas faces. Dark é o padrão; ambos recebem o mesmo cuidado. Abaixo, o mesmo card de insight da NINA renderizado nos dois temas ao mesmo tempo."
      >
        <div className="grid gap-4 lg:grid-cols-2">
          <ThemeFrame mode="light" />
          <ThemeFrame mode="dark" />
        </div>
      </Section>

      {/* ── 2. Gradiente-assinatura ───────────────────────────────────────── */}
      <Section
        index="02"
        title="Gradiente-assinatura"
        desc="O ativo de marca mais importante depois do logo. Extraído dos gradientes do símbolo. Use em heróis, CTA de marca e na NINA — nunca em texto longo ou fundos chapados."
      >
        <div className="dv-ring-gradient relative h-28 overflow-hidden rounded-3xl">
          <div className="dv-animated-gradient h-full w-full bg-[length:200%_200%]" />
        </div>
        <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
          {[
            { c: "#FF00C8", n: "Magenta", r: "A faísca" },
            { c: "#7A2BD6", n: "Violeta", r: "A ponte" },
            { c: "#1466E3", n: "Azul", r: "A confiança" },
            { c: "#1ED4F9", n: "Ciano", r: "A clareza" },
          ].map((s) => (
            <div
              key={s.c}
              className="rounded-2xl border border-gray-100 bg-white p-3 dark:border-white/10 dark:bg-navy-800"
            >
              <div className="h-10 w-full rounded-lg" style={{ background: s.c }} />
              <p className="mt-2 text-sm font-bold text-navy-700 dark:text-white">{s.n}</p>
              <p className="font-mono text-[11px] text-gray-700 dark:text-gray-400">{s.c}</p>
              <p className="text-[11px] text-gray-500 dark:text-gray-500">{s.r}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* ── 3. Escalas de cor ─────────────────────────────────────────────── */}
      <Section
        index="03"
        title="Paleta — escalas dv-* e ink-*"
        desc="Versões sólidas das paradas + neutros premium. Para texto colorido use 600/700 (light) e 300/400 (dark); os 500 puros brilham como preenchimento, gráfico e luz."
      >
        <div className="flex flex-col gap-4">
          <SwatchScale name="dv-magenta" hexes={["#FFE6F8", "#FFBFEE", "#FF8CE0", "#FF59D2", "#FF26C9", "#FF00C8", "#D100A4", "#A30080", "#75005C", "#470038"]} />
          <SwatchScale name="dv-blue" hexes={["#E7F0FE", "#C4DBFD", "#91BCFB", "#5E9CF8", "#2F7CF0", "#1466E3", "#0F50C2", "#0B3F9C", "#0A357F", "#0A2C66"]} />
          <SwatchScale name="dv-cyan" hexes={["#E6F7FE", "#C0ECFD", "#8DDDFB", "#54CCFA", "#22BAFA", "#1CA0F1", "#1180C8", "#0E6399", "#0D4E78", "#0E415F"]} />
          <SwatchScale name="ink" hexes={["#F5F6FA", "#E6E8F0", "#C9CDDD", "#9AA0BC", "#5E6588", "#3A3F5C", "#23263B", "#15172A", "#0D0E1C", "#070710"]} />
        </div>
      </Section>

      {/* ── 4. Tipografia ─────────────────────────────────────────────────── */}
      <Section
        index="04"
        title="Tipografia — Geist + Space Grotesk"
        desc="Corpo e UI em Geist (a mesma fonte do site datavalue.com.br). Títulos, heróis e números em Space Grotesk — larga e forte. Números sempre tabular-nums."
      >
        <div className="grid gap-4 lg:grid-cols-[1.4fr_1fr]">
          {/* Display — Space Grotesk */}
          <div className="rounded-3xl border border-gray-100 bg-white p-6 dark:border-white/10 dark:bg-navy-800">
            <p className="font-display text-xs font-bold uppercase tracking-[0.18em] text-dv-blue-600 dark:text-dv-cyan-300">
              Display · Space Grotesk
            </p>
            <h2 className="mt-3 font-display text-5xl font-bold leading-[1.02] tracking-[-0.03em] text-navy-700 dark:text-white">
              Dados <span className="dv-gradient-text">sob medida</span>
            </h2>
            <p className="mt-4 font-display text-3xl font-bold tracking-tight text-navy-700 dark:text-white">
              Título de página
            </p>
            <div className="mt-4 flex flex-wrap items-baseline gap-x-5 gap-y-1 font-display text-2xl text-navy-700 dark:text-white">
              <span className="font-normal">Regular 400</span>
              <span className="font-medium">Medium 500</span>
              <span className="font-bold">Bold 700</span>
            </div>
            <p className="mt-5 font-display text-4xl font-bold tabular-nums text-navy-700 dark:text-white">
              R$ 1.284.560 <span className="text-dv-cyan-500">·</span> 98,2%
            </p>
          </div>

          {/* Corpo — Geist */}
          <div className="rounded-3xl border border-gray-100 bg-white p-6 dark:border-white/10 dark:bg-navy-800">
            <p className="font-display text-xs font-bold uppercase tracking-[0.18em] text-dv-blue-600 dark:text-dv-cyan-300">
              Corpo · Geist
            </p>
            <p className="mt-3 font-sans text-base font-semibold leading-relaxed text-navy-700 dark:text-white">
              Subtítulo · Geist semibold
            </p>
            <p className="mt-2 font-sans text-sm font-medium leading-relaxed text-gray-700 dark:text-gray-300">
              Corpo em Geist: a clareza vem antes da decoração. O dado é o herói, e cada pixel de
              cor é uma decisão. Geist é limpa e legível mesmo em tabelas densas — por isso fica no
              corpo, enquanto a Space Grotesk, mais larga e forte, brilha nos títulos.
            </p>
            <p className="mt-3 font-sans text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
              Legenda · text-[11px]
            </p>
            <p className="mt-3 font-sans text-sm tabular-nums text-gray-700 dark:text-gray-300">
              Tabela: 1.024,5 h · 182,5 h · R$ 92.400,00
            </p>
          </div>
        </div>
      </Section>

      {/* ── 5. Botões ─────────────────────────────────────────────────────── */}
      <Section
        index="05"
        title="Botões"
        desc="Primário em TINTA/branco (CTA padrão, estilo Vertex). Gradiente reservado a momentos de marca. Clique = active:scale-[.98]."
      >
        <div className="flex flex-wrap items-center gap-3 rounded-3xl border border-gray-100 bg-white p-6 dark:border-white/10 dark:bg-navy-800">
          <button
            type="button"
            className="flex items-center justify-center gap-2 rounded-xl bg-ink-900 px-5 py-2.5 text-sm font-bold text-white shadow-dv-md transition-all hover:bg-ink-800 active:scale-[.98] dark:bg-white dark:text-ink-900 dark:hover:bg-gray-100"
          >
            Entrar <MdArrowForward className="h-4 w-4" />
          </button>
          <button
            type="button"
            className="dv-sheen flex items-center justify-center gap-1.5 rounded-xl bg-dv-gradient px-5 py-2.5 text-sm font-bold text-white shadow-dv-glow transition-all active:scale-[.98]"
          >
            <MdAutoAwesome className="h-4 w-4" /> Perguntar à NINA
          </button>
          <button
            type="button"
            className="flex items-center justify-center gap-1.5 rounded-xl border border-gray-200 px-5 py-2.5 text-sm font-semibold text-gray-700 transition-all hover:bg-gray-50 active:scale-[.98] dark:border-white/15 dark:text-gray-200 dark:hover:bg-white/5"
          >
            Secundário
          </button>
          <button
            type="button"
            aria-label="Ação"
            className="rounded-xl p-2.5 text-gray-500 transition-colors hover:bg-gray-100 hover:text-dv-blue-600 dark:text-gray-400 dark:hover:bg-navy-700"
          >
            <MdBolt className="h-5 w-5" />
          </button>
          <button
            type="button"
            disabled
            className="flex items-center justify-center gap-2 rounded-xl bg-ink-900 px-5 py-2.5 text-sm font-bold text-white opacity-50 dark:bg-white dark:text-ink-900"
          >
            Desabilitado
          </button>
        </div>
      </Section>

      {/* ── 6. Inputs ─────────────────────────────────────────────────────── */}
      <Section index="06" title="Campos de formulário" desc="Sempre com label associado e anel de foco da marca.">
        <div className="grid gap-4 rounded-3xl border border-gray-100 bg-white p-6 dark:border-white/10 dark:bg-navy-800 sm:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <label htmlFor="ds-search" className="text-sm font-semibold text-gray-700 dark:text-gray-300">
              Buscar cliente
            </label>
            <div className="relative">
              <MdSearch className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" aria-hidden="true" />
              <input
                id="ds-search"
                type="text"
                placeholder="Digite um nome…"
                className="w-full rounded-xl border border-gray-200 bg-white py-2.5 pl-9 pr-3 text-sm text-gray-800 transition-all focus:border-dv-blue-500 focus:outline-none focus:ring-2 focus:ring-dv-blue-500/20 dark:border-white/10 dark:bg-navy-900 dark:text-white"
              />
            </div>
          </div>
          <div className="flex flex-col gap-1.5">
            <label htmlFor="ds-type" className="text-sm font-semibold text-gray-700 dark:text-gray-300">
              Tipo de cobrança
            </label>
            <select
              id="ds-type"
              className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-800 transition-all focus:border-dv-blue-500 focus:outline-none focus:ring-2 focus:ring-dv-blue-500/20 dark:border-white/10 dark:bg-navy-900 dark:text-white"
            >
              <option>Recorrência</option>
              <option>Pacote de horas</option>
              <option>Projeto fechado</option>
            </select>
          </div>
        </div>
      </Section>

      {/* ── 7. Badges & status ────────────────────────────────────────────── */}
      <Section index="07" title="Badges & status" desc="Status nunca dependem só de cor — sempre com ícone/texto.">
        <div className="flex flex-wrap items-center gap-2.5 rounded-3xl border border-gray-100 bg-white p-6 dark:border-white/10 dark:bg-navy-800">
          <Chip className="bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300">
            <MdCheckCircle className="h-3.5 w-3.5" /> Ativo
          </Chip>
          <Chip className="bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
            <MdWarningAmber className="h-3.5 w-3.5" /> Pendente
          </Chip>
          <Chip className="bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">
            <MdInfoOutline className="h-3.5 w-3.5" /> Informação
          </Chip>
          <Chip className="bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300">
            <MdErrorOutline className="h-3.5 w-3.5" /> Erro
          </Chip>
          <Chip className="bg-gray-100 text-gray-600 dark:bg-navy-700 dark:text-gray-300">Neutro</Chip>
          <span className="dv-ring-gradient inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-semibold text-navy-700 dark:text-white">
            <MdAutoAwesome className="h-3.5 w-3.5 text-dv-magenta-500" /> NINA
          </span>
        </div>
      </Section>

      {/* ── 8. Superfícies ────────────────────────────────────────────────── */}
      <Section index="08" title="Cards & superfícies" desc="Profundidade por luz: card base, vidro, moldura de IA e aura.">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <SurfaceCard title="Card base" sub="shadow-dv-md · rounded-[20px]" className="border border-gray-100 bg-white nina-surface shadow-dv-md dark:border-white/10 dark:bg-navy-800" />
          <SurfaceCard title="Glass" sub=".dv-glass · blur premium" className="dv-glass shadow-dv-md" />
          <SurfaceCard title="Ring gradient" sub=".dv-ring-gradient · IA" className="dv-ring-gradient bg-white dark:bg-navy-800" />
          <SurfaceCard title="Aura" sub=".dv-aura · destaque" className="dv-aura border border-gray-100 bg-white nina-surface shadow-dv-md dark:border-white/10 dark:bg-navy-800" />
        </div>
      </Section>

      {/* ── 9. Herói de KPIs ──────────────────────────────────────────────── */}
      <Section index="09" title="Herói de KPIs" desc="O único bloco com gradiente cheio: topo de dashboard. KPIs em vidro sobre a marca.">
        <div className="relative overflow-hidden rounded-3xl bg-dv-gradient p-6 text-white shadow-dv-glow">
          <div className="pointer-events-none absolute -right-16 -top-20 h-64 w-64 rounded-full bg-white/10 blur-3xl" />
          <div className="relative flex items-center justify-between">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-white/70">Painel financeiro</p>
              <h3 className="text-xl font-extrabold">Junho 2026</h3>
            </div>
            <span className="rounded-full bg-white/15 px-3 py-1 text-xs font-medium text-white ring-1 ring-white/20 backdrop-blur">
              24 clientes ativos
            </span>
          </div>
          <div className="relative mt-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
            <KpiTile icon={<MdTrendingUp className="h-5 w-5" />} label="Previsto do mês" value="R$ 1,28M" sub="1.024 h apontadas" />
            <KpiTile icon={<MdCheckCircle className="h-5 w-5" />} label="Realizado" value="R$ 1,15M" sub="90% do previsto" />
            <KpiTile icon={<MdReceiptLong className="h-5 w-5" />} label="Faturado" value="R$ 980k" sub="18 cobranças" />
            <KpiTile icon={<MdWarningAmber className="h-5 w-5" />} label="Sem faturamento" value="3" sub="requer atenção" />
          </div>
        </div>
      </Section>

      {/* ── 10. Tabela ────────────────────────────────────────────────────── */}
      <Section index="10" title="Tabela premium" desc="Header em maiúsculas, linhas com hover, números tabular-nums, badges de status.">
        <div className="overflow-x-auto rounded-2xl border border-gray-100 bg-white nina-surface shadow-dv-md dark:border-white/10 dark:bg-navy-800">
          <table className="w-full min-w-[520px] text-left">
            <thead>
              <tr className="bg-gray-50 dark:bg-navy-900">
                {["Cliente", "Horas", "Previsto", "Status"].map((h) => (
                  <th key={h} className="px-4 py-3 text-[11px] font-bold uppercase tracking-wide text-gray-700 dark:text-gray-300">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[
                { c: "Aurora Tech", h: "182,5 h", v: "R$ 92.400", s: "ok" },
                { c: "Meridian Co.", h: "96,0 h", v: "R$ 48.000", s: "warn" },
                { c: "Nimbus Labs", h: "240,0 h", v: "R$ 120.000", s: "ok" },
              ].map((r) => (
                <tr key={r.c} className="border-b border-gray-100 transition-colors hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-navy-700">
                  <td className="px-4 py-3 text-sm font-semibold text-navy-700 dark:text-white">{r.c}</td>
                  <td className="px-4 py-3 text-sm tabular-nums text-gray-700 dark:text-gray-300">{r.h}</td>
                  <td className="px-4 py-3 text-sm font-bold tabular-nums text-navy-700 dark:text-white">{r.v}</td>
                  <td className="px-4 py-3">
                    {r.s === "ok" ? (
                      <Chip className="bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300">
                        <MdCheckCircle className="h-3.5 w-3.5" /> Faturado
                      </Chip>
                    ) : (
                      <Chip className="bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
                        <MdWarningAmber className="h-3.5 w-3.5" /> Pendente
                      </Chip>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      {/* ── 11. NINA ──────────────────────────────────────────────────────── */}
      <Section index="11" title="NINA · copiloto de IA" desc="A única superfície com gradiente animado livre. Aura + hairline em gradiente distinguem a inteligência do conteúdo humano.">
        <div className="dv-ring-gradient dv-aura rounded-3xl bg-white/85 p-6 backdrop-blur dark:bg-navy-800/85">
          <div className="flex items-start gap-3">
            <span className="dv-animated-gradient animate-dv-pulse-glow mt-1 h-9 w-9 shrink-0 rounded-2xl bg-[length:200%_200%] shadow-dv-glow" />
            <div className="min-w-0">
              <p className="flex items-center gap-2 text-sm font-bold text-navy-700 dark:text-white">
                NINA
                <span className="inline-flex items-center gap-1 rounded-full bg-dv-gradient-soft px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-dv-blue-600 dark:text-dv-cyan-300">
                  <MdInsights className="h-3 w-3" /> insight
                </span>
              </p>
              <p className="mt-1.5 text-sm leading-relaxed text-gray-700 dark:text-gray-300">
                Encontrei <strong className="font-bold text-navy-700 dark:text-white">3 clientes sem faturamento</strong> em
                junho, somando <strong className="font-bold text-navy-700 dark:text-white">R$ 184k</strong> previstos.
                Quer que eu gere as cobranças agora?
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <button type="button" className="dv-sheen flex items-center gap-1.5 rounded-lg bg-dv-gradient px-3 py-1.5 text-xs font-bold text-white shadow-dv-glow active:scale-[.98]">
                  <MdReceiptLong className="h-3.5 w-3.5" /> Gerar cobranças
                </button>
                <button type="button" className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-semibold text-gray-700 transition-colors hover:bg-gray-50 dark:border-white/15 dark:text-gray-200 dark:hover:bg-white/5">
                  Ver clientes
                </button>
                <span className="ml-auto flex items-center gap-1 text-[11px] text-gray-400">
                  <MdAccessTime className="h-3 w-3" /> baseado em dados de hoje
                </span>
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* ── 12. Elevação, raio & motion ───────────────────────────────────── */}
      <Section index="12" title="Elevação · raio · movimento" desc="Profundidade por luz, cantos generosos, movimento sutil e com propósito.">
        <div className="grid gap-4 lg:grid-cols-3">
          <div className="rounded-3xl border border-gray-100 bg-white p-5 dark:border-white/10 dark:bg-navy-800">
            <p className="mb-3 text-xs font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400">Elevação</p>
            <div className="flex flex-col gap-3">
              <div className="rounded-xl bg-white p-3 text-xs font-medium text-gray-700 shadow-dv-sm dark:bg-navy-900 dark:text-gray-300">shadow-dv-sm</div>
              <div className="rounded-xl bg-white p-3 text-xs font-medium text-gray-700 shadow-dv-md dark:bg-navy-900 dark:text-gray-300">shadow-dv-md</div>
              <div className="rounded-xl bg-white p-3 text-xs font-medium text-gray-700 shadow-dv-glow dark:bg-navy-900 dark:text-gray-300">shadow-dv-glow</div>
            </div>
          </div>
          <div className="rounded-3xl border border-gray-100 bg-white p-5 dark:border-white/10 dark:bg-navy-800">
            <p className="mb-3 text-xs font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400">Raio</p>
            <div className="flex flex-wrap items-end gap-3">
              {[
                { r: "rounded-xl", l: "xl" },
                { r: "rounded-2xl", l: "2xl" },
                { r: "rounded-[20px]", l: "primary" },
                { r: "rounded-3xl", l: "3xl" },
                { r: "rounded-4xl", l: "4xl" },
              ].map((x) => (
                <div key={x.l} className="text-center">
                  <div className={`h-12 w-12 bg-dv-gradient-soft ${x.r} ring-1 ring-dv-blue-500/20`} />
                  <p className="mt-1 text-[10px] text-gray-500 dark:text-gray-400">{x.l}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="rounded-3xl border border-gray-100 bg-white p-5 dark:border-white/10 dark:bg-navy-800">
            <p className="mb-3 text-xs font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400">Movimento</p>
            <div className="flex flex-col gap-2 text-xs font-medium text-gray-700 dark:text-gray-300">
              <span className="rounded-lg bg-gray-50 px-3 py-2 dark:bg-navy-900">animate-fade-up · entrada</span>
              <span className="rounded-lg bg-gray-50 px-3 py-2 dark:bg-navy-900">transition-all duration-200 · estado</span>
              <span className="rounded-lg bg-gray-50 px-3 py-2 dark:bg-navy-900">active:scale-[.98] · clique</span>
              <span className="rounded-lg bg-gray-50 px-3 py-2 dark:bg-navy-900">animate-dv-pulse-glow · NINA</span>
            </div>
          </div>
        </div>
      </Section>

      <footer className="mt-10 border-t border-gray-100 pt-6 text-center text-xs text-gray-500 dark:border-white/10 dark:text-gray-400">
        Tokens em <span className="font-mono">tailwind.config.js</span> +{" "}
        <span className="font-mono">src/index.css</span>. Documentação em{" "}
        <span className="font-mono">BRANDING-BOOK.md</span> e{" "}
        <span className="font-mono">DESIGN-SYSTEM.md</span>. · DataValue · Do dado bruto à decisão.
      </footer>
    </section>
  );
};

/* ── Helpers ─────────────────────────────────────────────────────────────── */

const Section: React.FC<{ index: string; title: string; desc: string; children: React.ReactNode }> = ({
  index,
  title,
  desc,
  children,
}) => (
  <div className="mt-8">
    <div className="mb-4 flex items-start gap-3">
      <span className="dv-gradient-text mt-0.5 font-mono text-sm font-bold">{index}</span>
      <div>
        <h2 className="text-lg font-extrabold tracking-tight text-navy-700 dark:text-white">{title}</h2>
        <p className="mt-0.5 max-w-3xl text-sm text-gray-700 dark:text-gray-400">{desc}</p>
      </div>
    </div>
    {children}
  </div>
);

const SwatchScale: React.FC<{ name: string; hexes: string[] }> = ({ name, hexes }) => {
  const steps = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900];
  return (
    <div>
      <p className="mb-1.5 font-mono text-xs font-bold text-navy-700 dark:text-gray-200">{name}</p>
      <div className="flex overflow-hidden rounded-xl ring-1 ring-black/5 dark:ring-white/10">
        {hexes.map((h, i) => (
          <div key={h} className="group relative flex-1" style={{ background: h }} title={`${name}-${steps[i]} · ${h}`}>
            <div className="h-12" />
            <span
              className={`absolute inset-x-0 bottom-1 text-center text-[9px] font-bold ${
                i < 5 ? "text-black/50" : "text-white/80"
              }`}
            >
              {steps[i]}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

const Chip: React.FC<{ className?: string; children: React.ReactNode }> = ({ className = "", children }) => (
  <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold ${className}`}>
    {children}
  </span>
);

const SurfaceCard: React.FC<{ title: string; sub: string; className?: string }> = ({ title, sub, className = "" }) => (
  <div className={`rounded-2xl p-5 ${className}`}>
    <p className="text-sm font-bold text-navy-700 dark:text-white">{title}</p>
    <p className="mt-1 text-[11px] text-gray-700 dark:text-gray-400">{sub}</p>
  </div>
);

const KpiTile: React.FC<{ icon: React.ReactNode; label: string; value: string; sub: string }> = ({
  icon,
  label,
  value,
  sub,
}) => (
  <div className="rounded-2xl bg-white/10 p-4 ring-1 ring-white/15 backdrop-blur transition-colors hover:bg-white/[0.16]">
    <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-white/15 text-white">{icon}</span>
    <p className="mt-2 text-[11px] font-medium text-white/70">{label}</p>
    <p className="font-display text-2xl font-bold leading-tight tracking-tight tabular-nums text-white">
      {value}
    </p>
    <p className="mt-0.5 text-[11px] text-white/60">{sub}</p>
  </div>
);

// Frame de comparação: renderiza o mesmo conteúdo num tema fixo (independente do
// tema global) usando as variáveis --dv-* inline + tokens var(--dv-*).
const ThemeFrame: React.FC<{ mode: "light" | "dark" }> = ({ mode }) => {
  const vars = mode === "light" ? LIGHT_VARS : DARK_VARS;
  return (
    <div
      className={`${mode === "dark" ? "dark" : ""} overflow-hidden rounded-3xl ring-1 ring-black/5`}
      style={{ ...vars, background: "var(--dv-canvas)" }}
    >
      <div className="flex items-center justify-between px-5 pt-4">
        <span className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider" style={{ color: "var(--dv-text-subtle)" }}>
          {mode === "light" ? <MdLightMode className="h-3.5 w-3.5" /> : <MdDarkMode className="h-3.5 w-3.5" />}
          {mode === "light" ? "Light" : "Dark"}
        </span>
        <span className="font-mono text-[10px]" style={{ color: "var(--dv-text-subtle)" }}>
          {mode === "light" ? "#F4F7FE" : "#070710"}
        </span>
      </div>
      <div className="p-5">
        <div
          className="dv-ring-gradient relative rounded-2xl p-4"
          style={{ background: "var(--dv-surface)" }}
        >
          <div className="flex items-start gap-2.5">
            <span className="dv-animated-gradient animate-dv-pulse-glow mt-0.5 h-7 w-7 shrink-0 rounded-xl bg-[length:200%_200%]" />
            <div>
              <p className="text-sm font-bold" style={{ color: "var(--dv-text)" }}>
                NINA
              </p>
              <p className="mt-1 text-[13px] leading-relaxed" style={{ color: "var(--dv-text-muted)" }}>
                3 clientes sem faturamento · R$ 184k previstos.
              </p>
            </div>
          </div>
          <div className="mt-3 flex gap-2">
            <button type="button" className="dv-sheen rounded-lg bg-dv-gradient px-3 py-1.5 text-xs font-bold text-white shadow-dv-glow">
              Gerar
            </button>
            <button
              type="button"
              className="rounded-lg px-3 py-1.5 text-xs font-semibold"
              style={{ color: "var(--dv-text)", border: "1px solid var(--dv-border)" }}
            >
              Ver
            </button>
          </div>
        </div>
        <div className="mt-3 flex items-center gap-2">
          {["#FF00C8", "#1466E3", "#1ED4F9"].map((c) => (
            <span key={c} className="h-5 flex-1 rounded-md" style={{ background: c }} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default DesignSystemPage;
